import { useEffect, useState, useCallback, useRef } from "react";
import { shallowEqual } from "react-redux";
import {
  confirmVerificationCode,
  fetchPhoneCountryCodes,
  sendVerificationCode,
  canResendCode,
} from "app/actions/user-settings";
import { editPhone, fetchSelf } from "app/actions/user";
import { useActions, useSelector } from "app/utils/hooks";
import { RootState } from "typedefs";
import { IUserPhone } from "app/interfaces/UserTypes";
import UserModel from "app/models/UserModel";
import UserSettingsModel from "app/models/UserSettingsModel";

interface Props {
  handlePhoneModal: () => void;
}

type ErrorType = {
  sendingCode: boolean;
  codeIsNotCorrect: boolean;
  somethingWentWrong: boolean;
  alreadyTaken: boolean;
};

type PhoneInfoType = {
  phone: string;
  phoneCountry: string;
  phoneNormalized?: string;
};

type VerificationResponseType = {
  id: string;
  _id: string;
  object: string;
  status: string;
  token: string | null;
  verified_at: string | null;
  can_retry: boolean;
};

type ReturnType = {
  countryCodeErr: ErrorType;
  lowercaseCountryCodes: string[];
  code: string;
  setCode: (value: string) => void;
  handleSendVerificationCode: () => void;
  isSendingCode: boolean;
  sendVerificationHandler: (value: string, data: IUserPhone) => void;
  handleTimerEnd: () => void;
  confirmCodeHandler: () => void;
  showResend: boolean;
  step: number;
  closeModal: () => void;
  error: ErrorType;
  self: UserModel;
  phoneInfo: PhoneInfoType;
};

export const useAccountPhoneVerificationModel = ({
  handlePhoneModal,
}: Props): ReturnType => {
  const actions = useActions({
    fetchPhoneCountryCodes,
    sendVerificationCode,
    confirmVerificationCode,
    canResendCode,
    editPhone,
    fetchSelf,
  });

  const { countryCodes, countryCodeErr } = useSelector(
    (state: RootState): UserSettingsModel => state.userSettings.fetch,
    shallowEqual,
  );

  const { self } = useSelector(
    (state: RootState): UserModel => state.user,
    shallowEqual,
  );

  const [step, setStep] = useState<number>(1);
  const [lowercaseCountryCodes, setLowercaseCountryCodes] = useState<string[]>(
    [],
  );
  const [phoneInfo, setPhoneInfo] = useState<PhoneInfoType>({
    phone: "",
    phoneCountry: "",
    phoneNormalized: "",
  });
  const [error, setError] = useState<ErrorType>({
    sendingCode: false,
    codeIsNotCorrect: false,
    somethingWentWrong: false,
    alreadyTaken: false,
  });
  const [code, setCode] = useState<string>("");
  const [verificationResponse, setVerificationResponse] =
    useState<VerificationResponseType | null>(null);
  const [showResend, setShowResend] = useState(false);
  const [isSendingCode, setIsSendingCode] = useState(false);

  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const verificationIdRef = useRef<string | null>(null);

  const clearPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  const checkCanResend = useCallback(async () => {
    const verificationId = verificationIdRef.current;

    if (!verificationId) {
      return;
    }

    try {
      const response = await actions.canResendCode(verificationId);

      if (response?.canRetry === true) {
        setShowResend(true);
        clearPolling();
      }
    } catch (err) {
      console.error("Someting went wrong during checkCanResend");
    }
  }, [actions, clearPolling]);

  useEffect(() => {
    if (verificationResponse?.id) {
      clearPolling();

      verificationIdRef.current =
        verificationResponse.id || verificationResponse._id;

      setShowResend(false);

      checkCanResend();

      const interval = setInterval(() => {
        checkCanResend();
      }, 35000);

      pollingIntervalRef.current = interval;
    }

    return () => {
      clearPolling();
    };
  }, [verificationResponse?.id, checkCanResend, clearPolling]);

  useEffect(() => {
    return () => {
      clearPolling();
    };
  }, [clearPolling]);

  const closeModal = useCallback(() => {
    setStep(1);
    clearPolling();
    verificationIdRef.current = null;
    setShowResend(false);
    setVerificationResponse(null);
    handlePhoneModal();
  }, [handlePhoneModal, clearPolling]);

  useEffect(() => {
    actions.fetchPhoneCountryCodes();
  }, [actions]);

  useEffect(() => {
    if (countryCodes) {
      const lowercaseCodes = countryCodes?.map((code: string): string =>
        code?.toLowerCase(),
      );

      if (self?.phoneVerified || typeof self?.phoneCountry === "string") {
        lowercaseCodes.push(self?.phoneCountry?.toLowerCase());
      }

      setLowercaseCountryCodes(lowercaseCodes);
    }
  }, [countryCodes, self]);

  const sendVerificationHandler = useCallback(
    (value: string, data: IUserPhone) => {
      const phoneNormalized = value;
      const phone = value.substring(data?.dialCode?.length);
      const phoneCountry = data?.countryCode?.toUpperCase();

      setPhoneInfo({ phoneNormalized, phone, phoneCountry });
    },
    [],
  );

  const handleSendVerificationCode = useCallback(async () => {
    // One request at a time: the Send button is disabled while this is true,
    // and Resend calls this same handler.
    if (isSendingCode) {
      return;
    }

    if (!phoneInfo.phone) {
      setError((prev) => ({
        ...prev,
        somethingWentWrong: true,
      }));
      return;
    }

    setIsSendingCode(true);

    try {
      const response = await actions.sendVerificationCode({
        phone: phoneInfo?.phone,
        phone_country: phoneInfo?.phoneCountry,
      });

      setVerificationResponse(response);

      if (step === 1) {
        setStep((prevStep) => prevStep + 1);
      }

      setError({
        sendingCode: false,
        codeIsNotCorrect: false,
        somethingWentWrong: false,
        alreadyTaken: false,
      });
    } catch (err) {
      setError((prev) => ({
        ...prev,
        sendingCode: true,
      }));
    } finally {
      setIsSendingCode(false);
    }
  }, [actions, phoneInfo, step, isSendingCode]);

  const handleTimerEnd = useCallback(() => {
    setShowResend(true);
  }, []);

  const fetchConfirmVerifCode = async () => {
    try {
      await actions.confirmVerificationCode({
        id: verificationResponse.id,
        code,
      });
    } catch (err) {
      if (err?.errors?.[0] === "sms_gateway_invalid_verification_code") {
        setError({
          codeIsNotCorrect: true,
          sendingCode: false,
          somethingWentWrong: false,
          alreadyTaken: false,
        });
      } else {
        setError({
          codeIsNotCorrect: false,
          sendingCode: false,
          somethingWentWrong: true,
          alreadyTaken: false,
        });
      }
      return false;
    }

    return true;
  };

  const fetchEditPhone = async () => {
    try {
      await actions.editPhone({
        verification_token: verificationResponse.token,
      });
    } catch (err) {
      if (err?.errors?.[0] === "phone_already_taken") {
        setError({
          alreadyTaken: true,
          sendingCode: false,
          codeIsNotCorrect: false,
          somethingWentWrong: false,
        });
      } else {
        setError({
          alreadyTaken: false,
          sendingCode: false,
          codeIsNotCorrect: false,
          somethingWentWrong: true,
        });
      }
      return false;
    }

    return true;
  };

  const confirmCodeHandler = async () => {
    if (!code || !verificationResponse?.id) {
      return;
    }

    const isCodeConfirmed = await fetchConfirmVerifCode();
    if (!isCodeConfirmed) {
      return;
    }

    const isPhoneEdited = await fetchEditPhone();

    clearPolling();
    verificationIdRef.current = null;

    if (!isPhoneEdited) {
      return;
    }

    if (step === 2) {
      closeModal();
    }

    await actions.fetchSelf();

    setError({
      sendingCode: false,
      codeIsNotCorrect: false,
      somethingWentWrong: false,
      alreadyTaken: false,
    });

    setShowResend(false);
  };

  return {
    countryCodeErr,
    lowercaseCountryCodes,
    code,
    setCode,
    sendVerificationHandler,
    handleSendVerificationCode,
    isSendingCode,
    handleTimerEnd,
    confirmCodeHandler,
    showResend,
    step,
    closeModal,
    error,
    self,
    phoneInfo,
  };
};
